/* eslint-disable max-len */
import React, {
    useState,
    useCallback,
    useContext,
    useEffect
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { LoadSaveService, MLGAFInfoService, MLPortalSettingsService } from 'gw-capability-quoteandbind';
import { LoadSaveService as GatewayLoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLLocalDateUtil, MLProductCodeUtil } from 'ml-portals-util-js';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLGafUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLGAFInfoService as GatewayMLGAFInfoService } from 'gw-capability-gateway';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MLModalService } from 'ml-portals-services-react';
import { QuestionSetComponent, platformMessages } from 'ml-components-platform-react';
import { useTranslator } from '@jutro/locale';
import metadata from './MLEmployersAffiliationsEligibilityComponent.metadata.json5';
import styles from './MLEmployersAffiliationsEligibilityComponent.module.scss';
import messages from './MLEmployersAffiliationsEligibilityComponent.messages';
import appConfig from 'app-config';

const QUESTIONS_TO_SHOW = [
    'HOPOccupationType_Ext',
    'HOPEducationLevel_ML'
];
const DWELLING_Q_SET = 'HOPDwellingAddlInfo_ML';

const TAB_BREAKPOINT = 768;

function MLEmployersAffiliationsEligibilityComponent(props) {
    const gafPath = 'baseData.gafInfo_ML';
    const gafPathValue = 'baseData.gafInfo_ML.value';
    const noEfileVerRequiredCode = 'eligible_no_efile_ver_required';
    const efileVerRequired = 'eligible_efile_ver_required';

    const {
        submissionVm,
        selectOrganization,
        updateWizardData,
        setDisableNextButton,
        id,
        onValidate,
        isPaymentScreen,
        isEndorsement,
        isGpa,
        isPaymentMethodPayrollDeduct,
        onVerifyEligibility,
        showErrors,
        isSkipping,
        eligibilityCheckOnInitComplete,
        isCurrentPayPlanPayroll,
        handleAutoEligSelection,
        handleAutoEligReset,
        eligibilityInfos,
        organization
    } = props;

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const { authHeader } = useAuthentication();
    const { EndorsementService } = useDependencies('EndorsementService');
    const gafInfo = _.get(submissionVm, `${gafPath}.value`, {});
    
    const translator = useTranslator();

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const { 
        validateEmployeeNumberVisibility,
        validateDepartmentNumberVisibility,
        validateAlternateIdVisibility,
        validateEmployeeDepartmentNumberVisibility,
        validateSsnField
     } = MLGafUtil();

    const {
        clearBlockAlerts,
        addErrorAlert,
        addWarningAlert,
        clearSpecificWarningAlert
    } = MLNotificationBlockUtil();

    const [getFailedToUpdateGafInfo, setFailedToUpdateGafInfo] = useState(false);
    const quoteId = _.get(submissionVm, 'quoteID.value') || _.get(submissionVm, 'jobID.value');
    const sessionId = _.get(submissionVm, 'sessionUUID.value', undefined);
    const productCode = _.get(submissionVm, 'baseData.productCode.value', '');
    const [isFormPristine, setIsFormPristine] = useState(true);
    const employeeInfoPath =_.get(submissionVm, 'baseData.employeeInfo_ML.value');
    const gafPathMl =_.get(submissionVm, 'baseData.gafInfo_ML');
    const pni =  _.get(submissionVm, 'baseData.value.primaryNamedInsured_ML', undefined);
    const customerEligibilityFileIndicator = _.get(submissionVm, `${gafPathValue}.customerEligibilityFileIndicator`, false)
    const [enableInvalidSSNValidation, setEnableInvalidSSNValidation] = useState(false);

    const viewModelService = useContext(ViewModelServiceContext);

    const isQnb = appConfig.portalCode === 'QB';
    const { mercerOrganization } = appConfig;
    const { mercerGpcCode } = appConfig;

    const isMobile = () => {
        return window.innerWidth <= TAB_BREAKPOINT;
    };

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid, submissionVm]);

       useEffect(()=> {
                MLPortalSettingsService.getSettings(authHeader).then((response) => {
                   setEnableInvalidSSNValidation(!!response?.enableInvalidSSNValidation_Ext);
                 });
        },[])

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVm, path, value);
            updateWizardData(submissionVm);
            if (setDisableNextButton) {
                setDisableNextButton(false);
            }
        },
        [submissionVm, updateWizardData]
    );

    const gafInfoObject = {
                gafInfo : _.get(submissionVm, gafPath, {}),
            }

    const updateGAFInfo = useCallback((executeOnlyIfPreviousUpdateFailed) => {
        const gafVm = _.get(submissionVm, gafPath, {});
        if (gafVm.aspects.valid && gafVm.aspects.subtreeValid) {
            if (!executeOnlyIfPreviousUpdateFailed || getFailedToUpdateGafInfo) {
                gafVm.value.isEditMode = false;

                if(gafVm?.employeeNumber?.value === "") {
                    gafVm.employeeNumber.value = null;
                }

                if(gafVm?.departmentNumber?.value === "") {
                    gafVm.departmentNumber.value = null;
                }

                if(gafVm?.employeeDepartmentNumber?.value === "") {
                    gafVm.employeeDepartmentNumber.value = null;
                }

                let gafPromise;
                if (isGpa) {
                    gafPromise = GatewayMLGAFInfoService.gpaUpdateGAFInfo(gafVm.value, quoteId, authHeader);
                } else if (isEndorsement) {
                    gafPromise = MLGAFInfoService.updateGAFInfoForEndorsement(gafVm.value, quoteId, authHeader);
                } else {
                    gafPromise = MLGAFInfoService.updateGAFInfo(gafVm.value, sessionId, quoteId);
                }
                showProgressDialog(platformMessages.loading, gafPromise, true);
                gafPromise.then((res) => {
                    setIsFormPristine(false);
                    setFailedToUpdateGafInfo(false);
                    res.isEditMode = true;
                    const newVm = viewModelService.clone(submissionVm);
                    _.set(newVm, gafPathValue, res);

                    if(isQnb && isPaymentScreen && !_.isEmpty(employeeInfoPath?.name) && gafVm?.alternateId?.value) {                        
                        _.set(newVm, "baseData.gafInfo_ML.alternateId.value", gafVm?.alternateId?.value);
                    }

                    updateWizardData(newVm);
                });
            }
        } else {
            setFailedToUpdateGafInfo(true);
        }
    }, [submissionVm, getFailedToUpdateGafInfo, isGpa, isEndorsement, showProgressDialog,
        quoteId, authHeader, sessionId, viewModelService, updateWizardData]);

    const onYearsAffiliatedBlurUpdate = useCallback(() => {
        updateGAFInfo(true);
    }, [updateGAFInfo]);

    const onFieldValueChange = useCallback((val, path) => {
        writeValue(val, path);
        updateGAFInfo(false);
    }, [updateGAFInfo, writeValue]);

    const isCorrespondencePromo = useCallback(() => {
        const correspondenceCodes = ['correspondence_email', 'correspondence_mail'];
        const selectedCorrespondenceCode = _.get(submissionVm, `${gafPathValue}.hearOfMetLife`);
        return correspondenceCodes.includes(selectedCorrespondenceCode);
    }, [submissionVm]);

    const isEmployer = useCallback(() => {
        const employerCodes = ['employer', 'preferredemployer'];
        const customerType = _.get(submissionVm, `${gafPathValue}.customerType`);
        return employerCodes.includes(customerType);
    }, [submissionVm]);

    const isAssociation = useCallback(() => {
        const employerCodes = ['association', 'affinity'];
        const customerType = _.get(submissionVm, `${gafPathValue}.customerType`);
        return employerCodes.includes(customerType);
    }, [submissionVm]);

    const isEligibleForAllIndicator = useCallback(() => {
        return _.get(submissionVm, `${gafPathValue}.isEligibleForAllIndicator`);
    }, [submissionVm]);

    const isEFileVerificationNeeded = useCallback(() => {
        const eligibleForPayrollDeductCode = _.get(submissionVm, `${gafPathValue}.eligibleForPayrollDeduct`);
        const eligibleForDirectBillCode = _.get(submissionVm, `${gafPathValue}.eligibleForDirectBill`);

        return eligibleForDirectBillCode === efileVerRequired || eligibleForPayrollDeductCode === efileVerRequired;
    }, [submissionVm]);

    const showEnterAboveMsg = useCallback(() => {
        const eligibilityVerifiedVal = _.get(submissionVm, `${gafPathValue}.eligibilityVerified`);
        return _.isUndefined(eligibilityVerifiedVal) && isEFileVerificationNeeded();
    }, [submissionVm, isEFileVerificationNeeded]);

    const showEligibilityVerifiedSuccessMessage = useCallback(() => {
        const eligibleForDirectBillCode = _.get(submissionVm, `${gafPathValue}.eligibleForDirectBill`);
        const eligibilityVerifiedVal = _.get(submissionVm, `${gafPathValue}.eligibilityVerified`);

        return eligibleForDirectBillCode === noEfileVerRequiredCode
        || (eligibleForDirectBillCode === efileVerRequired && eligibilityVerifiedVal === true);
    }, [submissionVm]);

    const showUnableToVerifyGroupDiscountWarning = useCallback(() => {
        return !isFormPristine && !showEligibilityVerifiedSuccessMessage() && customerEligibilityFileIndicator;
    }, [isFormPristine, showEligibilityVerifiedSuccessMessage]);

    const clearEligibilityFields = useCallback(() => {
        const newGafVM = viewModelService.clone(_.get(submissionVm, gafPath));
        _.set(newGafVM, 'eligibilityVerified.value', null);
        _.set(newGafVM, 'departmentNumber.value', null);
        _.set(newGafVM, 'employeeNumber.value', null);
        _.set(newGafVM, 'metroBadgeNumber.value', null);
        _.set(newGafVM, 'eligibilityVerificationDate.value', null);
        const newVm = viewModelService.clone(submissionVm);
        _.set(newVm, gafPathValue, newGafVM.value);
        updateWizardData(newVm);
    }, [viewModelService, submissionVm, updateWizardData]);

    const checkEligibility = useCallback((evt, path, ssnOrAltIdParam, isinitCheck = false) => {
        const gafVm = _.get(submissionVm, gafPath);        

        if(!_.isEmpty(employeeInfoPath?.name)) {
            return;
        }

        const ssnOrAltId = path?.value || ssnOrAltIdParam;
        if (ssnOrAltId) {
            let eligibilityPromise;
            if (isGpa) {
                eligibilityPromise = GatewayMLGAFInfoService.gpaCheckEligibility(gafVm.cdfNumber.value, ssnOrAltId, quoteId, authHeader);
            } else if (isEndorsement) {
                eligibilityPromise = MLGAFInfoService.checkEligibilityForEndorsement(gafVm.cdfNumber.value, ssnOrAltId, quoteId, authHeader);
            } else {
                eligibilityPromise = MLGAFInfoService.checkEligibility(gafVm.cdfNumber.value, ssnOrAltId, sessionId, quoteId);
            }
            showProgressDialog(platformMessages.checkingEligibility, eligibilityPromise, true);
            if (!isEFileVerificationNeeded()) {
                updateGAFInfo();
                if (eligibilityCheckOnInitComplete) {
                    eligibilityCheckOnInitComplete(isinitCheck);
                }
            } else {
                eligibilityPromise.then((res) => {
                    const newGafVM = viewModelService.clone(gafVm);
                    _.set(newGafVM, 'eligibilityVerified.value', res.eligibilityVerified);
                    _.set(newGafVM, 'paypointNumber.value', res.paypointNumber);
                    _.set(newGafVM, 'departmentNumber.value', res.departmentNumber);
                    _.set(newGafVM, 'employeeNumber.value', res.employeeNumber);
                    _.set(newGafVM, 'metroBadgeNumber.value', res.metroBadgeNumber);
                    if (res.eligibilityVerified) {
                        _.set(newGafVM, 'eligibilityVerificationDate.value', MLLocalDateUtil.date2LocalDate(new Date()));
                    }
                    setIsFormPristine(false);
                    const newVm = viewModelService.clone(submissionVm);
                    _.set(newVm, gafPathValue, newGafVM.value);
                    onVerifyEligibility()
                    updateWizardData(newVm);
                    if (eligibilityCheckOnInitComplete) {
                        eligibilityCheckOnInitComplete(isinitCheck);
                    }
                });
            }
        } else {
            clearEligibilityFields();
            if (eligibilityCheckOnInitComplete) {
                eligibilityCheckOnInitComplete(isinitCheck);
            }
        }
    }, [submissionVm, eligibilityCheckOnInitComplete, isGpa, isEndorsement, showProgressDialog, isEFileVerificationNeeded, quoteId, authHeader, sessionId, updateGAFInfo, viewModelService, onVerifyEligibility, updateWizardData, clearEligibilityFields]);

    const updateEligibility = useCallback((isInitCheck) => {
        const gafVmVal = _.get(submissionVm, gafPathValue, {});
        if (gafVmVal.eligibilityVerified) {
            const toUpdate = _.isUndefined(gafVmVal.eligibilityVerificationDate)
                || MLLocalDateUtil.compare(gafVmVal.eligibilityVerificationDate, MLLocalDateUtil.date2LocalDate(new Date())) !== 0;
            if (toUpdate) {
                const updateValue = gafVmVal.ssn || gafVmVal.alternateId;
                checkEligibility(undefined, undefined, updateValue, isInitCheck);
            } else if (eligibilityCheckOnInitComplete) {
                eligibilityCheckOnInitComplete(isInitCheck)
            }
        } else if (eligibilityCheckOnInitComplete) {
            eligibilityCheckOnInitComplete(isInitCheck)
        }
    }, [checkEligibility, eligibilityCheckOnInitComplete, submissionVm]);

    useEffect(() => {
        if(isSkipping || isQnb) {
            return;
        }
        
        const gafVmVal = _.get(submissionVm, gafPathValue, {});
        const isGpcCodeUndefined = _.isUndefined(gafVmVal.gpcCode) || _.isNull(gafVmVal.gpcCode);
        
        setIsFormPristine(isGpcCodeUndefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifyEligiblity = useCallback(async (id, isSSN) => {        
        const lastName = pni?.lastName
        const postalCode = pni?.primaryAddress?.postalCode?.substring(0,5);

        showProgressDialog(platformMessages.loading);
        let eligPromise = await MLGAFInfoService.verifyEmployee(lastName,postalCode,id,isSSN,sessionId,quoteId,authHeader);
        hideProgressDialog();

        if(eligPromise && !_.isEmpty(eligPromise)){
            handleAutoEligSelection(true,eligPromise,id);
        } else {
            handleAutoEligReset();

            if(!isSSN){
                addWarningAlert(translator(messages.altIdVerifError, {
                    altIDLabel: _.get(submissionVm, `${gafPathValue}.alternateEmployeeIdentifierLiteral`, false) || messages.alternateId
                }),"altIdVerifError");
            } else{
                addWarningAlert(translator(messages.altIdVerifError, {
                    altIDLabel: translator(messages.last4SSNVerifError)
                }),"altIdVerifError");                
            }

        }          
    },[eligibilityInfos, submissionVm]);

    const onSSNVerifBlur = useCallback(async (value) => {
        clearBlockAlerts("ssnVerifyErr");
        clearSpecificWarningAlert("altIdVerifError");

        const inputVal = value?.target?.value;
        
        if(inputVal?.length < 4) {
            addErrorAlert(messages.ssnValidation,"ssnVerifyErr");
            return;
        }

        verifyEligiblity(inputVal, true);

    },[verifyEligiblity]);

    const onAltIDVerifBlur = useCallback((value) => {
        clearSpecificWarningAlert("altIdVerifError");

        const inputVal = value?.target?.value

        if(inputVal) {
            verifyEligiblity(inputVal, false);
        }
    },[verifyEligiblity]);

    const getYearsAffiliatedLabel = useCallback(() => {
        return isEmployer() ? messages.yearsAffiliatedEmployer : messages.yearsAffiliatedAssociation;
    }, [isEmployer]);

    const syncQuestionSets = useCallback((aQuestionSetAnswersValue, path) => {
        let questionSetPromise;
        if (isGpa && isEndorsement) {
            questionSetPromise = EndorsementService.syncQuestionSetML([quoteId, [aQuestionSetAnswersValue]], authHeader)
        } else if (isGpa) {
            questionSetPromise = GatewayLoadSaveService.syncQuestionSetML(quoteId, [aQuestionSetAnswersValue], authHeader)
        } else {
            questionSetPromise = LoadSaveService.syncQuestionSetML(quoteId, [aQuestionSetAnswersValue])
        }   
        showProgressDialog(platformMessages.syncingQuestions, questionSetPromise, true);
        questionSetPromise.then((updatedQuestions) => {
            const dwellingQs = updatedQuestions.find((q) => q.code === DWELLING_Q_SET);
            const newVm = viewModelService.clone(submissionVm);
            _.set(newVm, path, dwellingQs);
            updateWizardData(newVm);
        });
    }, [isGpa, isEndorsement, showProgressDialog, EndorsementService, quoteId, authHeader, viewModelService, submissionVm, updateWizardData]);

    const showEmployeeDepartmentNumberField = () => {
        return !_.isUndefined(_.get(submissionVm, `${gafPathValue}.employeeDepartmentNumberLength`))
            && (!_.isEmpty(employeeInfoPath?.ssn) 
            || (isPaymentScreen && isPaymentMethodPayrollDeduct()) 
            || (isEndorsement && isPaymentMethodPayrollDeduct()))
    };

    const showAlternateIdField = () => {
        const isAltUsageInd = _.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false);
        const isEmpDepLengthEmpty = _.isUndefined(_.get(submissionVm, `${gafPathValue}.employeeDepartmentNumberLength`));

        return (isAltUsageInd && isEFileVerificationNeeded() && isEmpDepLengthEmpty)
            && (!_.isEmpty(employeeInfoPath?.ssn) 
            || (isPaymentScreen && isPaymentMethodPayrollDeduct()) 
            || (isEndorsement && isPaymentMethodPayrollDeduct()))
    };

    const generateVisibilityOverrides = useCallback(() => {
        return {
            gpcRequiredFieldsContainer: {
                visible: !(_.isUndefined(_.get(submissionVm, `${gafPathValue}.gpcCode`)) || _.isNull(_.get(submissionVm, `${gafPathValue}.gpcCode`))),
                className: `${isQnb ? "radioButtonQnB" : ""} ${isPaymentScreen ? "vertical-spacing-30" : ""}`
            },
            hearAboutMetlife: {
                visible: false
            },
            promoCode: {
                visible: false
            },
            employmentStatus: {
                visible: !isEligibleForAllIndicator(),
                disabled: isEndorsement && isCurrentPayPlanPayroll()
            },
            membershipStatus: {
                visible: isEligibleForAllIndicator(),
                disabled: isEndorsement && isCurrentPayPlanPayroll()
            },
            yearsAffiliated: {
                label: getYearsAffiliatedLabel(),
                disabled: isEndorsement && isCurrentPayPlanPayroll(),
                className: `col-md-4 ${isQnb || 'label-nowrap-override'}`
            },
            partOfManagement: {
                visible: _.get(submissionVm, `${gafPathValue}.partOfManagementAvailable`, false) && !(isEndorsement && isPaymentMethodPayrollDeduct()),
                isHorizontal: !isMobile(),
                className: isMobile() ? 'radiobutton-alignment col-md-4' : 'col-md-4'
            },
            memberOfUnion: {
                visible: _.get(submissionVm, `${gafPathValue}.memberOfUnionAvailable`, false) && !(isEndorsement && isPaymentMethodPayrollDeduct()),
                isHorizontal: !isMobile(),
                className: isMobile() ? 'radiobutton-alignment col-md-4' : 'col-md-4'
            },
            temporaryEmployee: {
                visible: _.get(submissionVm, `${gafPathValue}.temporaryEmployeeAvailable`, false) && !(isEndorsement && isPaymentMethodPayrollDeduct()),
                isHorizontal: !isMobile(),
                className: isMobile() ? 'radiobutton-alignment col-md-4' : 'col-md-4'
            },
            alternateId: {
                label: _.get(submissionVm, `${gafPathValue}.alternateEmployeeIdentifierLiteral`, false) || messages.alternateId,
                visible: !isGpa ? false : validateAlternateIdVisibility(gafPathMl,employeeInfoPath,isPaymentScreen,isEndorsement,isPaymentMethodPayrollDeduct()),
                disabled: !_.isEmpty(employeeInfoPath?.ssn),
            },
            employeeDepartmentNumber: {
                label: _.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false)?
                            _.get(submissionVm, `${gafPathValue}.alternateEmployeeIdentifierLiteral`, false) || messages.employeeNumber
                            : _.get(submissionVm, `${gafPathValue}.employeeNumberLiteral`, false) || messages.employeeNumber,
                visible: !isGpa ? false : validateEmployeeDepartmentNumberVisibility(gafPathMl,employeeInfoPath,isPaymentScreen,isEndorsement,isPaymentMethodPayrollDeduct()),
                disabled: !_.isEmpty(employeeInfoPath?.ssn),
            },
            employeeNumber: {
                label: _.get(submissionVm, `${gafPathValue}.employeeNumberLiteral`, false) || messages.employeeNumber,
                visible: !isGpa ? false : validateEmployeeNumberVisibility(gafPathMl, employeeInfoPath),
                disabled: ((!_.isEmpty(employeeInfoPath?.employeeNumber) && !_.isEmpty(employeeInfoPath?.name))
                || (isEndorsement && organization?.name?.toLowerCase().includes(mercerOrganization) &&  gafInfo?.gpcCode === mercerGpcCode)
            ) ? true : false
            },
             departmentNumber: {
                label: _.get(submissionVm, `${gafPathValue}.departmentNumberLiteral`, false) || messages.departmentNumber,
                visible: !isGpa? false : validateDepartmentNumberVisibility(gafPathMl, employeeInfoPath),
                disabled: (!_.isEmpty(employeeInfoPath?.departmentNumber) && !_.isEmpty(employeeInfoPath?.name)) ? true : false
            },
            ssn: {
                visible: !isGpa ? false : !_.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false) && isEFileVerificationNeeded(),
                validationMessages: validateSsnField(gafInfoObject, enableInvalidSSNValidation),
                disabled: (isEndorsement && (isPaymentMethodPayrollDeduct() || organization?.name?.toLowerCase().includes(mercerOrganization) &&  gafInfo?.gpcCode === mercerGpcCode))
                || (!_.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false) && !_.isEmpty(employeeInfoPath?.name)),
                label: isGpa ? translator(messages.ssn) : translator(messages.ssnOptional),
                disabled: !_.isEmpty(employeeInfoPath?.ssn),
                tooltip: isGpa ? {
                    title: translator(messages.whyEnterSSN),
                    text: translator(messages.ssnToolTip),
                    placement: "bottom"
                } : undefined
            },
            ssnContainer: {
                visible: isGpa && !_.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false) && isEFileVerificationNeeded()
            },
            ssnTooltipContainer: {
                visible: !isGpa,
                tooltipContent: translator(messages.ssnToolTip)
            },
            alternateIdVerif: {
                visible: isQnb ? isPaymentScreen && _.get(submissionVm, `${gafPathValue}.customerEligibilityFileIndicator`, false) && _.get(submissionVm, `${gafPathValue}.alternateIdUsage`, false) && !(_.isUndefined(_.get(submissionVm, `${gafPathValue}.gpcCode`)) || _.isNull(_.get(submissionVm, `${gafPathValue}.gpcCode`))) : false,
                label: _.get(submissionVm, `${gafPathValue}.alternateEmployeeIdentifierLiteral`, false) || messages.alternateId,
                required: !_.isEmpty(employeeInfoPath?.name)
            },
            ssnVerif: {
                visible: isQnb ? isPaymentScreen && _.get(submissionVm, `${gafPathValue}.customerEligibilityFileIndicator`, false) && !_.get(submissionVm, `${gafPathValue}.alternateIdUsage`, true) && !(_.isUndefined(_.get(submissionVm, `${gafPathValue}.gpcCode`)) || _.isNull(_.get(submissionVm, `${gafPathValue}.gpcCode`))) : false,
                label: translator(messages.last4SSN),
                required: !_.isEmpty(employeeInfoPath?.name)
            },
            prefillScriptContainer: {
                visible: isQnb ? false : showEnterAboveMsg()
            },
            verifySuccessMsg: {
                visible: false,
            },
            verifyWarningMsg: {
                visible: isQnb ? false : showUnableToVerifyGroupDiscountWarning()
            },
            qstSetContainer: {
                visible: false
            },
            dwellingQuestionSet: {
                visible: !isEndorsement,
                questionsToShowML: QUESTIONS_TO_SHOW,
                showHeaderML: false,
                onValueChange: syncQuestionSets
            },
            additionalInfoHeader: {
                visible: !(_.isUndefined(_.get(submissionVm, `${gafPathValue}.gpcCode`)) || _.isNull(_.get(submissionVm, `${gafPathValue}.gpcCode`))) && !isPaymentScreen,
                className: isQnb ? 'section-header padding-left-15 padding-top-15' : 'section-header padding-left-15'
            }
        };
    }, [submissionVm, isPaymentScreen, isEndorsement, isCorrespondencePromo, isPaymentMethodPayrollDeduct, getYearsAffiliatedLabel, isEFileVerificationNeeded, isGpa, translator, showEnterAboveMsg, showEligibilityVerifiedSuccessMessage, showUnableToVerifyGroupDiscountWarning, productCode, syncQuestionSets, isEligibleForAllIndicator]);

    const overrides = {
        ...generateVisibilityOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            questionset: QuestionSetComponent
        },
        resolveCallbackMap: {
            updateGafInfo: updateGAFInfo,
            //checkEligibility: checkEligibility,
            onValidate: onValidate,
            onYearsAffiliatedBlurUpdate,
            onFieldValueChange,
            onAltIDVerifBlur,
            onSSNVerifBlur
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={submissionVm}
            classNameMap={styles}
            componentMap={resolvers.resolveComponentMap}
            overrideProps={overrides}
            onValueChange={writeValue}
            callbackMap={resolvers.resolveCallbackMap}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
        />
    );
}


MLEmployersAffiliationsEligibilityComponent.propTypes = {
    submissionVm: PropTypes.shape({}).isRequired,
    selectOrganization: PropTypes.func.isRequired,
    updateWizardData: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    onValidate: PropTypes.func.isRequired,
    isPaymentScreen: PropTypes.bool,
    isEndorsement: PropTypes.bool,
    isGpa: PropTypes.bool,
    isPaymentMethodPayrollDeduct: PropTypes.func,
    onVerifyEligibility: PropTypes.func,
    eligibilityCheckOnInitComplete: PropTypes.func,
    isCurrentPayPlanPayroll: PropTypes.func,
    handleAutoEligSelection: PropTypes.func,
    handleAutoEligReset: PropTypes.func,
    eligibilityInfos: PropTypes.array
};

MLEmployersAffiliationsEligibilityComponent.defaultProps = {
    isPaymentScreen: false,
    isEndorsement: false,
    isGpa: false,
    isPaymentMethodPayrollDeduct: _.noop,
    onVerifyEligibility: _.noop,
    eligibilityCheckOnInitComplete: _.noop,
    isCurrentPayPlanPayroll: _.noop,
    handleAutoEligSelection: _.noop,
    handleAutoEligReset: _.noop,
    eligibilityInfos: []
};

export default MLEmployersAffiliationsEligibilityComponent;
